import { useRef, useEffect, useCallback } from 'react';
import { ISourceTimelineEventType } from '@24i/player-base';
import { usePlayerUIContext } from '../Context/ui';
import { bingeWatchingOverlayShow, bingeWatchingOverlayHide, bingeWatchingOverlaySetRemainingTime, } from '../Context/ui/actions';
const COUNTDOWN_SECONDS = 15;
const DEFAULT_CREDITS_OFFSET_SECONDS = 15;
const MINIMUM_DURATION_FOR_CREDITS_SECONDS = 60;
export const useBingeWatchingOverlay = ({ playerInstance, asset, hasNextEpisode, onGoToNextEpisode, }) => {
    const { state: { bingeWatchingOverlay: { isOpen, isDismissed, remainingTime }, }, dispatch, } = usePlayerUIContext();
    const isInCreditsRef = useRef(false);
    const tickTimerRef = useRef();
    const onHide = useCallback(() => {
        dispatch(bingeWatchingOverlayHide(true));
    }, []);
    const onNext = useCallback(() => {
        dispatch(bingeWatchingOverlayHide(false));
        if (!playerInstance)
            return;
        if (!playerInstance.currentAdBreak) {
            const allAds = (playerInstance.timeline || []).filter((te) => te.type === ISourceTimelineEventType.ADVERTISEMENT);
            const lastAd = allAds.length > 0 ? allAds[allAds.length - 1] : undefined;
            if (lastAd &&
                (typeof lastAd.start !== 'number' || lastAd.start > playerInstance.currentTime)) {
                playerInstance.seek(lastAd.start || playerInstance.duration);
                return;
            }
        }
        if (onGoToNextEpisode) {
            playerInstance.pause();
            onGoToNextEpisode(asset);
        }
    }, [asset]);
    useEffect(() => {
        isInCreditsRef.current = false;
        dispatch(bingeWatchingOverlayHide(false));
    }, [asset === null || asset === void 0 ? void 0 : asset.id]);
    useEffect(() => {
        const shouldCheck = !!(playerInstance && hasNextEpisode);
        if (!shouldCheck) {
            return () => {
            };
        }
        const onTimeUpdate = () => {
            const duration = Math.floor(playerInstance.duration / 1000) * 1000;
            const currentTime = Math.floor(playerInstance.currentTime / 1000) * 1000;
            const timeline = playerInstance.timeline || [];
            const credits = timeline.filter(({ type, start }) => type === ISourceTimelineEventType.CREDITS &&
                start < duration - DEFAULT_CREDITS_OFFSET_SECONDS * 1000);
            if (!credits.length &&
                duration &&
                duration >= MINIMUM_DURATION_FOR_CREDITS_SECONDS * 1000) {
                credits.push({
                    type: ISourceTimelineEventType.CREDITS,
                    start: duration - DEFAULT_CREDITS_OFFSET_SECONDS * 1000,
                    end: duration,
                });
            }
            const isInCredits = credits.some((timelineEvent) => {
                const { start } = timelineEvent;
                return currentTime >= start - 500;
            });
            const isInAdvertisement = (!!playerInstance.currentAdBreak && !playerInstance.canSkipAd) ||
                (!playerInstance.live &&
                    playerInstance.seekableRange.start === playerInstance.seekableRange.end);
            const nextIsInCredits = isInCredits && !isInAdvertisement;
            if (nextIsInCredits === isInCreditsRef.current) {
                return;
            }
            isInCreditsRef.current = nextIsInCredits;
            const secondsFromEnd = Math.floor((duration - currentTime) / 1000);
            if (nextIsInCredits) {
                dispatch(bingeWatchingOverlayShow(Math.min(COUNTDOWN_SECONDS, secondsFromEnd)));
            }
            else {
                dispatch(bingeWatchingOverlayHide(false));
            }
        };
        playerInstance.addEventListener('timeupdate', onTimeUpdate);
        return () => playerInstance.removeEventListener('timeupdate', onTimeUpdate);
    }, [playerInstance, hasNextEpisode]);
    useEffect(() => {
        if (!isOpen) {
            clearTimeout(tickTimerRef.current);
            return () => { };
        }
        if (remainingTime === 0) {
            onNext();
        }
        else if (remainingTime > 0) {
            tickTimerRef.current = setTimeout(() => {
                dispatch(bingeWatchingOverlaySetRemainingTime(remainingTime - 1));
            }, 1000);
        }
        return () => clearTimeout(tickTimerRef.current);
    }, [isOpen, remainingTime]);
    return {
        isOpen,
        isDismissed,
        remainingTime,
        onHide,
        onNext,
    };
};
//# sourceMappingURL=useBingeWatchingOverlay.js.map